
import { Mic, Upload, Lightbulb } from "lucide-react";
import { CaptureThought } from "./thoughts/CaptureThought";
import { UploadDialog } from "./upload/UploadDialog";
import { RecordingSection } from "./recording/RecordingSection";
import { useState } from "react";
import React from "react";
import { trackEvent } from "../../utils/analytics";

interface DefaultViewProps {
  setActiveSection: (section: string) => void;
}

export function DefaultView({ setActiveSection }: DefaultViewProps) {
  const [isTyping, setIsTyping] = useState(false);
  const [isThoughtModalOpen, setIsThoughtModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [showRecording, setShowRecording] = useState(false);

  const handleShowRecording = () => {
    try {
      trackEvent('recording_section_opened');
      setShowRecording(true);
    } catch (error) {
      console.error('Error opening recording section:', error);
      setShowRecording(true); // Still open the section even if tracking fails
    }
  };

  const handleOpenUploadModal = () => {
    try {
      trackEvent('upload_modal_opened');
      setIsUploadModalOpen(true);
    } catch (error) {
      console.error('Error opening upload modal:', error);
      setIsUploadModalOpen(true); // Still open the modal even if tracking fails
    }
  };

  const handleOpenThoughtModal = () => {
    try {
      trackEvent('thought_modal_opened');
      setIsThoughtModalOpen(true);
    } catch (error) {
      console.error('Error opening thought modal:', error);
      setIsThoughtModalOpen(true); // Still open the modal even if tracking fails
    }
  };

  if (showRecording) {
    return (
      <RecordingSection 
        setActiveSection={() => setShowRecording(false)} 
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 max-w-2xl mx-auto w-full">
      <button
        onClick={handleShowRecording}
        className="flex items-center gap-4 py-4 pl-10 pr-4 bg-slate-200 hover:bg-slate-300 border-2 border-slate-300 dark:bg-slate-700/50 dark:hover:bg-slate-700 dark:border-slate-600 rounded-full transition-colors duration-200"
      >
        <div>
          <Mic className="h-5 w-5 md:h-6 md:w-6 text-foreground" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-bold text-foreground md:text-lg">Recording</div>
          <div className="text-xs md:text-sm text-muted-foreground">Record a new meeting.</div>
        </div>
      </button>

      <button
        onClick={handleOpenUploadModal}
        className="flex items-center gap-4 py-4 pl-10 pr-4 bg-slate-200 hover:bg-slate-300 border-2 border-slate-300 dark:bg-slate-700/50 dark:hover:bg-slate-700 dark:border-slate-600 rounded-full transition-colors duration-200"
      >
        <div>
          <Upload className="h-5 w-5 md:h-6 md:w-6 text-foreground" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-bold text-base text-foreground md:text-lg">Upload</div>
          <div className="text-xs md:text-sm text-muted-foreground">Upload a recording or transcript.</div>
        </div>
      </button>

      <button
        onClick={handleOpenThoughtModal}
        className="flex items-center gap-4 py-4 pl-10 pr-4 bg-slate-200 hover:bg-slate-300 border-2 border-slate-300 dark:bg-slate-700/50 dark:hover:bg-slate-700 dark:border-slate-600 rounded-full transition-colors duration-200"
      >
        <div>
          <Lightbulb className="h-5 w-5 md:h-6 md:w-6 text-foreground" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-bold text-foreground md:text-lg">Thought</div>
          <div className="text-xs md:text-sm text-muted-foreground">Capture a new thought.</div>
        </div>
      </button>

      <CaptureThought 
        isTyping={isTyping}
        setIsTyping={setIsTyping}
        open={isThoughtModalOpen}
        onOpenChange={setIsThoughtModalOpen}
      />

      <UploadDialog
        open={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
      />
    </div>
  );
}
